import { EventBus } from '@/shared/app/connector/EventBus';
import Template from '@/shared/app/model/templating/Template';
import { CommunicationsFilteringMode } from '@/shared/uni/templating/components/communications/types';
import { makeUniCommunications } from '@/shared/uni/templating/components/communications/ui/maker';
import {
  getActiveCommunicationsFromDataset,
  sortCommunicationsByName,
} from '@/shared/uni/templating/components/communications/ui/utils';
import { isNull, isString } from 'lodash';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import AppConstants from '../../model/AppConstants';
import { useStore } from '../../model/store/store';

export const DEFAULT_BASE_CONTENT_DURATION_MS = 12000;
export const DEFAULT_DISENGAGE_MODE = 'replace';

const parseDisengageMode = (mode: unknown): 'replace' | 'alternate' =>
  mode?.toString() === 'alternate' ? 'alternate' : DEFAULT_DISENGAGE_MODE;

export const useDisengageConfiguration = () => {
  const store = useStore();
  const interval = ref(-1);
  const forceUpdate = ref(0);
  const commIndex = ref(0);

  const zones = computed(() => {
    const hasDisengageDataset = Object.keys(store.state.datasets).includes(
      AppConstants.COMM_DISENGAGE_DATASET_NAME,
    );
    if (!hasDisengageDataset) return null;
    const values = store.state.datasets[AppConstants.COMM_DISENGAGE_DATASET_NAME];
    return isString(values.zones) ? values.zones.split(',') : [];
  });

  const disengageSettings = computed(() => {
    const values = store.state.datasets[AppConstants.COMM_DISENGAGE_DATASET_NAME] ?? {};
    const parsedDuration = Number.parseInt(values.baseContentDurationMs?.toString() ?? '', 10);
    return {
      mode: parseDisengageMode(values.mode),
      baseContentDurationMs: Number.isFinite(parsedDuration)
        ? parsedDuration
        : DEFAULT_BASE_CONTENT_DURATION_MS,
    };
  });

  const activeCommunications = computed(() => {
    // eslint-disable-next-line no-unused-expressions
    forceUpdate.value;
    if (isNull(zones.value)) return [];
    if (!Object.keys(store.state.datasets).includes(AppConstants.COMM_DISENGAGE_DATASET_NAME))
      return [];
    const dataset = store.state.datasets.communications;
    const communications = getActiveCommunicationsFromDataset({
      dataset,
      zones: zones.value,
      mode: CommunicationsFilteringMode.MY_ZONES_ONLY,
    });
    return sortCommunicationsByName(communications);
  });

  watch(
    () => activeCommunications.value.length,
    (length) => {
      if (length === 0) {
        commIndex.value = 0;
        return;
      }
      commIndex.value %= length;
    },
  );

  const onCommEnd = () => {
    const { length } = activeCommunications.value;
    if (length > 0) {
      commIndex.value = (commIndex.value + 1) % length;
    }
  };

  onMounted(() => {
    interval.value = setInterval(() => {
      forceUpdate.value += 1;
    }, 60000);
    EventBus.$on(AppConstants.DISENGAGE_COMM_END_EVENT_NAME, onCommEnd);
  });

  onUnmounted(() => {
    clearInterval(interval.value);
    EventBus.$off(AppConstants.DISENGAGE_COMM_END_EVENT_NAME, onCommEnd);
  });

  const makeReplaceTemplate = () =>
    makeUniCommunications({
      zones: zones.value ?? [],
      mode: CommunicationsFilteringMode.MY_ZONES_ONLY,
      overrideDataset: {
        communications: JSON.stringify(activeCommunications.value) ?? '[]',
      },
    });

  const makeAlternateTemplate = (): Template => {
    const communication = activeCommunications.value[commIndex.value];
    return {
      component: 'Alternate',
      config: {
        contentTrigger: 'duration',
        contentDuration: String(disengageSettings.value.baseContentDurationMs),
        communicationTrigger: 'event',
        communicationEvent: AppConstants.DISENGAGE_COMM_END_EVENT_NAME,
      },
      slots: {
        content: store.getters.template as Template,
        communication: makeUniCommunications({
          zones: zones.value ?? [],
          mode: CommunicationsFilteringMode.MY_ZONES_ONLY,
          playMode: 'once',
          doneEvent: AppConstants.DISENGAGE_COMM_END_EVENT_NAME,
          overrideDataset: {
            communications: JSON.stringify(communication ? [communication] : []),
          },
        }),
      },
    };
  };

  return computed(() => {
    if (!activeCommunications.value.length || isNull(zones.value)) {
      return null;
    }

    if (disengageSettings.value.mode === 'alternate') {
      // eslint-disable-next-line no-unused-expressions
      commIndex.value;
      return makeAlternateTemplate();
    }

    return makeReplaceTemplate();
  });
};
